import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { dbManager } from '../services/db_manager';
import { useAuth } from './AuthContext';
import { Book } from '../types';
import { storageService } from '../services/storageService';
import { cacheManager } from '../services/cacheManager';
import { statsService } from '../services/statsService';
import { streakService } from '../services/streakService';
import { flashcardImportQueueService } from '../services/flashcardImportQueueService';
import { getLocalISOString } from '../utils/timezoneUtils';

const LIBRARY_HTML_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-atualizadas.json";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-online-htmls/";

interface Profile {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string;
    bio?: string;
    avatar_config?: any;
    plan_type?: 'free' | 'pro' | string;
    subscription_tier?: string;
    email?: string;
    city?: string;
    state?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    learning_languages?: string | string[];
    theme_color?: string;
    current_streak?: number;
    highest_streak?: number;
    last_computed_date?: string;
    native_language?: string;
}

interface Friendship {
    id: string;
    user_id: string;
    friend_id: string;
    status: string;
    sender?: Profile;
    receiver?: Profile;
}


interface DataContextType {
    profile: Profile | null;
    books: Book[];
    friends: (Profile & { friendshipId: string })[];
    pendingRequests: Friendship[];
    loading: boolean;
    isSyncing: boolean;
    refreshProfile: (force?: boolean, optimisticData?: any) => Promise<void>;
    refreshBooks: (force?: boolean) => Promise<void>;
    setBooks: React.Dispatch<React.SetStateAction<Book[]>>; // Added for reactive updates
    refreshFriends: () => Promise<void>;
    setFriends: React.Dispatch<React.SetStateAction<(Profile & { friendshipId: string })[]>>;
    setPendingRequests: React.Dispatch<React.SetStateAction<Friendship[]>>;
    setIsMutatingFriends: (isMutating: boolean) => void;
    refreshAll: () => Promise<void>;
    activeVoiceTotal: number;
    refreshActiveVoice: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useEffect(() => {
        // Start any pending background flashcard import tasks
        flashcardImportQueueService.processQueue();
    }, []);

    const { user, loading: authLoading } = useAuth();
    const [profile, setProfile] = useState<Profile | null>(null);
    const [books, setBooks] = useState<Book[]>(() => {
        try {
            const cached = localStorage.getItem('cache_books');
            return cached ? JSON.parse(cached) : [];
        } catch { return []; }
    });
    const [friends, setFriends] = useState<(Profile & { friendshipId: string })[]>(() => {
        try {
            const cached = localStorage.getItem('cache_friends');
            return cached ? JSON.parse(cached) : [];
        } catch { return []; }
    });
    const [pendingRequests, setPendingRequests] = useState<Friendship[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const isSyncingRef = useRef(false); // Lock obrigatório para evitar loop de re-render
    const lastFocusRefresh = React.useRef(0); // Debounce de foco
    const isMutatingFriendsRef = useRef(false);
    const [activeVoiceTotal, setActiveVoiceTotal] = useState<number>(0);

    const setIsMutatingFriends = useCallback((isMutating: boolean) => {
        isMutatingFriendsRef.current = isMutating;
    }, []);

    const refreshActiveVoice = useCallback(async () => {
        if (!user || user.isGuest) return;
        try {
            const voiceRes = await dbManager.execute({
                action: 'get_active_voice_total' as any,
                table: 'active_voice_sessions',
                user_id: user.id
            });
            if (voiceRes && voiceRes.total_seconds !== undefined) {
                setActiveVoiceTotal(voiceRes.total_seconds);
            }
        } catch (e) {}
    }, [user?.id]);

    // Conquistas: uma vez por sessão o servidor recalcula tudo a partir da atividade real (retroativo)
    useEffect(() => {
        if (!user?.id) return;
        import('../services/achievementService')
            .then(m => m.syncAchievementsOncePerSession(user.id))
            .catch(() => {});
    }, [user?.id]);

    const refreshProfile = useCallback(async (force = false, optimisticData?: any) => {
        if (!user || user.isGuest) {
            setProfile(null);
            return;
        }

        const cacheKey = `data_profile_${user.id}`;
        
        if (optimisticData) {
            // High priority: Inject the new data directly into the cache
            cacheManager.set(cacheKey, optimisticData);
            setProfile(optimisticData);
        } else if (force) {
            cacheManager.clear(cacheKey);
        }

        await cacheManager.swr(
            cacheKey,
            async () => {
                // Use authenticated select to get plan_type and all private fields
                const results = await dbManager.from('profiles', user.id).select({ id: user.id });
                let data = Array.isArray(results) ? results : (results?.data || []);
                
                // Fetch Streak Data from the dedicated 'streaks' table
                let streakData = null;
                try {
                    streakData = await streakService.getStreak(user.id);
                } catch (e) {
                    console.warn('[DataContext] Failed to fetch streak data:', e);
                }
                
                // --- AUTO INITIALIZATION: ONLY FOR BRAND NEW USERS (zero rows in D1) ---
                if (data.length === 0 && user.email) {
                    // Safety check: try one more time before writing, to protect against
                    // transient empty responses causing accidental data overwrites.
                    console.warn('[D1 SYNC] Zero rows found for user. Verifying before auto-init...');
                    const verifyResults = await dbManager.from('profiles', user.id).select({ id: user.id });
                    const verifyData = Array.isArray(verifyResults) ? verifyResults : (verifyResults?.data || []);

                    if (verifyData.length > 0) {
                        // Row actually exists. Transient empty response. Use the verify data.
                        console.warn('[D1 SYNC] Verify found existing row. Skipping auto-init.');
                        data = verifyData;
                    } else {
                        // Confirmed new user. Safe to create profile.
                        const emailPrefix = user.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
                        const randomSuffix = Math.floor(1000 + Math.random() * 9000);
                        const generatedUsername = `${emailPrefix}${randomSuffix}`;

                        const newProfile: Profile = {
                            id: user.id,
                            username: generatedUsername,
                            display_name: user.displayName || 'Usuário',
                            avatar_url: user.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${user.displayName}`,
                            email: user.email,
                            plan_type: 'free',
                            subscription_tier: 'free'
                        };

                        await dbManager.from('profiles', user.id).upsert({
                            ...newProfile,
                            updated_at: new Date().toISOString()
                        });

                        data = [newProfile];
                        console.log('[D1 SYNC] Auto-initialized profile for new user:', generatedUsername);
                    }
                }

                if (data && data.length > 0) {
                    const profData = { ...data[0] }; // Clone to avoid mutation issues
                    
                    if (profData.avatar_config && typeof profData.avatar_config === 'string') {
                        try { profData.avatar_config = JSON.parse(profData.avatar_config); } catch (e) { }
                    }
                    if (profData.learning_languages && typeof profData.learning_languages === 'string') {
                        try { profData.learning_languages = JSON.parse(profData.learning_languages); } catch (e) { }
                    }

                    // Log D1 data received for diagnostics
                    console.log('[D1 SYNC] Dados recebidos:', data[0]);

                    // --- BLINDAGEM: NÃO substituir dados do D1 com valores do Google ---
                    // Se o D1 já tem display_name salvo pelo usuário, preservar sempre.
                    // Apenas preencher avatar_url se vier vazio do D1 (campo não editável pela UI).
                    if (!profData.avatar_url && user.photoURL) {
                        profData.avatar_url = user.photoURL;
                    }
                    // DO NOT override display_name with Google values for existing users.
                    // DO NOT fallback username to email prefix for existing users.
                    // Both fields are exclusively managed by the user via Settings/Profile pages.

                    // Normalize plan_type: merge subscription_tier into plan_type
                    if (!profData.plan_type || profData.plan_type === 'free') {
                        if (profData.subscription_tier && profData.subscription_tier !== 'free') {
                            profData.plan_type = profData.subscription_tier;
                        }
                    }

                    // Ensure bio is never null
                    profData.bio = profData.bio || '';

                    // Integrate Streak Data
                    if (streakData) {
                        profData.current_streak = streakData.current_streak;
                        profData.highest_streak = streakData.highest_streak;
                        profData.last_computed_date = streakData.last_computed_date;
                    } else {
                        profData.current_streak = 0;
                    }

                    return profData;
                }
                return null;
            },
            (data: any) => {
                if (data) {
                    const normalized = { ...data, bio: data.bio || '' };
                    setProfile(normalized);
                    localStorage.setItem('cache_profile', JSON.stringify(normalized));
                }
            }
        );
    }, [user?.id]);



    const refreshBooks = useCallback(async (force = false) => {
        if (!user) return;

        if (user.isGuest) {
            const localBooks = storageService.getBooks();
            setBooks(localBooks);
            return;
        }

        if (isSyncingRef.current && !force) return;
        isSyncingRef.current = true;
        setIsSyncing(true);
        const cacheKey = `data_books_${user.id}`;
        
        if (force) {
            cacheManager.clear(cacheKey);
        }

        await cacheManager.swr(
            cacheKey,
            async () => {
                const booksData = await dbManager.from('books', user.id).select();
                const progressData = await dbManager.from('user_progress', user.id).select();
                const readBooksData = await dbManager.from('user_read_books', user.id).select().catch(() => []);

                const progressMap = new Map();
                if (progressData) {
                    progressData.forEach((p: any) => progressMap.set(p.book_id, p));
                }

                const readBookIds = new Set((readBooksData || []).map((r: any) => String(r.book_id)));

                if (!booksData) return [];

                const mappedBooks: Book[] = booksData.map((b: any) => {
                    const bId = b.custom_id || String(b.id);
                    const progInfo = progressMap.get(bId);
                    const isOnlineLibraryBook = /^\d+$/.test(bId);
                    const isBookCompleted = readBookIds.has(bId) || readBookIds.has(String(b.id)) || b.status === 'completed' || b.is_completed === 1;

                    let fileUrl = b.file_url || '';
                    let format = b.file_type;

                    if (isOnlineLibraryBook) {
                        format = 'html';
                        fileUrl = `${R2_HTML_BASE}${bId}.html`;
                    }

                    return {
                        id: bId,
                        custom_id: b.custom_id || bId,
                        title: b.title || 'Sem título',
                        author: b.author || 'Autor Desconhecido',
                        coverUrl: b.cover_url || '',
                        fileUrl: fileUrl,
                        lastPage: progInfo ? progInfo.current_page : (b.last_page || 1),
                        lastRead: progInfo?.last_read || b.created_at || new Date().toISOString(),
                        progress: progInfo ? progInfo.percentage : undefined,
                        totalPages: 0,
                        isFavorite: b.is_favorite || false,
                        status: isBookCompleted ? 'completed' : (b.status || 'reading'),
                        isCompleted: isBookCompleted,
                        dateAdded: new Date(b.created_at || Date.now()).getTime(),
                        category: (b.custom_id?.startsWith('club_')) ? 'clube_do_livro' : (isOnlineLibraryBook ? 'LibriTui Library (HTML)' : (b.file_type === 'html' ? 'Meus HTMLs' : 'Meus PDFs')),
                        language: b.language || 'und',
                        format: (
                            format === 'html' ||
                            isOnlineLibraryBook ||
                            (b.custom_id && b.custom_id.startsWith('gut-')) ||
                            (b.category && (b.category.toLowerCase().includes('html') || b.category.toLowerCase().includes('epub')))
                        ) ? 'html' : 'pdf'
                    };
                }).sort((a: any, b: any) => {
                    const dateA = new Date(a.lastRead).getTime();
                    const dateB = new Date(b.lastRead).getTime();
                    return dateB - dateA; // Descending
                });
                
                // 3. Get local books (Offline-First)
                const localBooks = storageService.getBooks();

                // 4. Merge Logic: Remote books + Local books that are NOT in remote
                const merged = [...mappedBooks];
                localBooks.forEach(lb => {
                    const exists = merged.some(rb => rb.id === lb.id || rb.custom_id === lb.id);
                    if (!exists) {
                        const isHtml = lb.format === 'html' || lb.format === 'epub' || lb.category?.toLowerCase().includes('html') || lb.category?.toLowerCase().includes('epub');
                        const isLocalCompleted = readBookIds.has(lb.id) || lb.status === 'completed' || lb.isCompleted;
                        merged.push({
                            ...lb,
                            status: isLocalCompleted ? 'completed' : (lb.status || 'reading'),
                            isCompleted: isLocalCompleted,
                            format: isHtml ? 'html' : 'pdf'
                        });
                    }
                });

                return merged;
            },
            (data: Book[], isCached: boolean) => {
                setBooks(data);
                if (isCached) setLoading(false);
                if (!isCached) setIsSyncing(false);
            }
        );
        setIsSyncing(false); 
        isSyncingRef.current = false;
    }, [user?.id]);

    const refreshFriends = useCallback(async () => {
        if (!user || user.isGuest) {
            setFriends([]);
            setPendingRequests([]);
            return;
        }

        if (isMutatingFriendsRef.current) {
            console.log("[DataContext] refreshFriends bypassed due to active mutation lock");
            return;
        }

        const cacheKey = `data_friends_${user.id}`;
        await cacheManager.swr(
            cacheKey,
            async () => {
                const allFriendships = await dbManager.from('friendships', user.id).select(
                    { OR_USER_FRIEND: user.id },
                    undefined,
                    'friendships'
                );
                return allFriendships || [];
            },
            (allFriendships: any[]) => {
                const pending = allFriendships.filter((f: any) => f.friend_id === user.id && f.status === 'pending')
                    .map((f: any) => ({
                        id: f.id,
                        user_id: f.user_id,
                        friend_id: f.friend_id,
                        status: f.status,
                        sender: {
                            id: f.user_id,
                            username: f.sender_username,
                            display_name: f.sender_display_name,
                            avatar_url: f.sender_avatar_url,
                        }
                    }));
                setPendingRequests(pending as any[]);

                const accepted = allFriendships.filter((f: any) => f.status === 'accepted')
                    .map((f: any) => {
                        const isReceiver = f.friend_id === user.id;
                        
                        // Parse configs safely
                        const parseConfig = (conf: any) => {
                            if (!conf) return null;
                            if (typeof conf === 'string') {
                                try { return JSON.parse(conf); } catch (e) { return null; }
                            }
                            return conf;
                        };

                        const profile = isReceiver ? {
                            id: f.user_id,
                            username: f.sender_username,
                            display_name: f.sender_display_name,
                            avatar_url: f.sender_avatar_url,
                            bio: f.sender_bio,
                            avatar_config: parseConfig(f.sender_avatar_config)
                        } : {
                            id: f.friend_id,
                            username: f.receiver_username,
                            display_name: f.receiver_display_name,
                            avatar_url: f.receiver_avatar_url,
                            bio: f.receiver_bio,
                            avatar_config: parseConfig(f.receiver_avatar_config)
                        };
                        return { ...profile, friendshipId: f.id };
                    });
                setFriends(accepted as any[]);
                localStorage.setItem('cache_friends', JSON.stringify(accepted));
            }
        );
    }, [user?.id]);

    const lastSyncRef = useRef<number>(0);

    const refreshAll = useCallback(async (force = false) => {
        if (isSyncingRef.current) return;
        
        const now = Date.now();
        // 30s Debounce logic requested by the user
        if (!force && now - lastSyncRef.current < 30000) {
            console.log("[DataContext] Skipping sync, last sync was less than 30s ago.");
            return;
        }

        isSyncingRef.current = true;
        setIsSyncing(true);
        lastSyncRef.current = now;

        try {
            // Removemos os setTimeouts de 1 segundo para acelerar a sincronização
            // e usamos Promise.all para as buscar que podem ser paralelas.
            await refreshProfile();
            
            if (user) {
                // Executamos em lote para ser mais rápido
                await Promise.all([
                    refreshBooks(true),
                    refreshFriends(),
                    refreshActiveVoice()
                ]);
            }
        } catch (e) {
            console.warn("DataContext: Staggered refresh managed issue:", e);
        } finally {
            setLoading(false);
            setIsSyncing(false);
            isSyncingRef.current = false;
        }
    }, [user?.id, refreshProfile, refreshBooks, refreshFriends, refreshActiveVoice]);

    useEffect(() => {
        // Initialize stats listeners (Batching, Flush, Retry)
        statsService.init();

        if (!authLoading && !isSyncingRef.current) {
            refreshAll();
        }

        // Porto Seguro 5.1: Revalidate on Focus
        const handleFocus = () => {
            if (!authLoading && user && !isSyncingRef.current) {
                // Debounce agressivo (mínimo 5s entre refreshes de foco)
                const now = Date.now();
                if (now - lastFocusRefresh.current < 5000) return;
                lastFocusRefresh.current = now;

                console.log("DataContext: Revalidating on focus (debounced)...");
                refreshBooks();
                refreshActiveVoice();
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [authLoading, user?.id, refreshAll]);

    useEffect(() => {
        if (!user || user.isGuest || !profile) return;

        // 1. Smart Location Sync
        const syncLocation = async () => {
            const hasLocation = profile.city || profile.latitude;
            if (!hasLocation) {
                try {
                    const { locationService } = await import('../services/locationService');
                    const loc = await locationService.getCurrentLocation();
                    if (loc) {
                        const { profileService } = await import('../services/profileService');
                        await profileService.updateProfile(user.id, loc);
                        setProfile(prev => prev ? { ...prev, ...loc } : null);
                    }
                } catch (e) {
                    console.warn("DataContext: Location sync failed:", e);
                }
            }
        };

        const syncActivity = async () => {
            const SYNC_KEY = `libritui_last_activity_streak_${user.id}`;
            const lastSync = parseInt(localStorage.getItem(SYNC_KEY) || '0', 10);
            const SIX_HOURS = 6 * 60 * 60 * 1000;

            if (Date.now() - lastSync > SIX_HOURS) {
                try {
                    await streakService.validateAndIncrementStreak(user.id, 'other');
                    localStorage.setItem(SYNC_KEY, Date.now().toString());
                    refreshProfile();
                } catch (e) {
                    console.warn("DataContext: Streak sync failed:", e);
                }
            }
        };

        syncLocation();
        // syncActivity(); // DESATIVADO TEMPORARIAMENTE conforme solicitado para estabilizar renderização
    }, [user?.id]);

    // Porto Seguro 5.3: Background sync for secondary services (Notes/Translations)
    // Isolado do loop principal para evitar bombardeio e loops de re-render
    useEffect(() => {
        if (!user || user.isGuest) return;
        
        const backgroundSync = async () => {
            try {
                // Sincronizar decks de notas
                const { notesDeckManager } = await import('../services/notesDeckManager');
                await notesDeckManager.syncAllFromD1();
                
                // Sincronizar cache de traduções com delay maior
                const { translationService } = await import('../services/translationService');
                await new Promise(resolve => setTimeout(resolve, 10000)); // 10s para garantir paz na rede
                await translationService.syncTranslationsCacheFromD1();
            } catch (e) {
                console.warn("DataContext: Background sync failed:", e);
            }
        };

        backgroundSync();
    }, [user?.id]);
    
    // Porto Seguro 5.4: Foreground Push Notifications Reactivity
    useEffect(() => {
        let unsubscribe = () => {};
        import('../services/firebase').then(({ subscribeToMessages }) => {
            unsubscribe = subscribeToMessages((payload) => {
                console.log("[DataContext] Firebase WebPush Event:", payload);
                window.dispatchEvent(new CustomEvent('libritui-new-message', { detail: payload }));
                import('../services/soundService').then(m => m.soundService.play('notification')).catch(() => {});
            });
        }).catch(e => console.warn('Could not launch message observer', e));
        
        return () => unsubscribe();
    }, []);

    const contextValue = React.useMemo(() => ({
        profile,
        books,
        friends,
        pendingRequests,
        loading,
        isSyncing,
        refreshProfile,
        refreshBooks,
        setBooks,
        refreshFriends,
        setFriends,
        setPendingRequests,
        setIsMutatingFriends,
        refreshAll,
        activeVoiceTotal,
        refreshActiveVoice
    }), [
        profile,
        books,
        friends,
        pendingRequests,
        loading,
        isSyncing,
        refreshProfile,
        refreshBooks,
        setBooks,
        refreshFriends,
        setFriends,
        setPendingRequests,
        setIsMutatingFriends,
        refreshAll,
        activeVoiceTotal,
        refreshActiveVoice
    ]);

    return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
